/**
 * The color of a banner button (the call-to-action pill of the email banners,
 * layout/BannerButton). The scene keeps one color for each banner, in the
 * `buttonColors` record of the images context. A banner without a value shows
 * its default look, which is light.
 */
export const BUTTON_COLORS = ["light", "dark"] as const;

export type ButtonColor = (typeof BUTTON_COLORS)[number];
export type ButtonColors = Record<string, ButtonColor>;

const BUTTON_COLOR_SET = new Set<string>(BUTTON_COLORS);

function assertButtonColor(value: unknown): asserts value is ButtonColor {
  if (typeof value !== "string" || !BUTTON_COLOR_SET.has(value)) {
    throw new Error(`Invalid button color: ${String(value)}`);
  }
}

/** The next color of the cycle: light → dark → light. No value is light. */
export function nextButtonColor(current: unknown): ButtonColor {
  if (current === undefined) return "dark";
  assertButtonColor(current);
  return current === "light" ? "dark" : "light";
}

/** Check the `buttonColors` of a scene. A scene without them has none. */
export function normalizeButtonColors(value: unknown): ButtonColors {
  if (value === undefined || value === null) return {};
  if (typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Invalid button colors: expected an object");
  }
  const colors: ButtonColors = {};
  for (const [templateId, color] of Object.entries(value)) {
    assertButtonColor(color);
    colors[templateId] = color;
  }
  return colors;
}
